// src/pages/tenant/TenantSecurityPage.tsx
// Security configuration page for a tenant.

import { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useTenant, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Badge,
  ErrorAlert,
} from '@react/components';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SecuritySettings {
  require_2fa: boolean;
  sso_enabled: boolean;
  allowed_domains: string[];
  allowed_ip_ranges: string[];
  email_notifications_enabled: boolean;
  slack_integration_enabled: boolean;
}

// ---------------------------------------------------------------------------
// Toggle component
// ---------------------------------------------------------------------------

function Toggle({
  checked,
  onChange,
  label,
  description,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
  description?: string;
}) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${
          checked ? 'bg-primary' : 'bg-muted'
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition-transform ${
            checked ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
      <div className="min-w-0">
        <span className="text-sm font-medium text-foreground">{label}</span>
        {description && (
          <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
        )}
      </div>
    </label>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * TenantSecurityPage - security settings for a tenant.
 * Route: /tenants/:id/settings/security
 */
export const TenantSecurityPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useTenant();
  const { t } = useI18n();

  const [settings, setSettings] = useState<SecuritySettings>({
    require_2fa: false,
    sso_enabled: false,
    allowed_domains: [],
    allowed_ip_ranges: [],
    email_notifications_enabled: true,
    slack_integration_enabled: false,
  });

  const [loadedSettings, setLoadedSettings] = useState<any>({});
  const [newDomain, setNewDomain] = useState('');
  const [newIpRange, setNewIpRange] = useState('');
  const [saving, setSaving] = useState<string | null>(null);
  const [successCard, setSuccessCard] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<unknown>(null);

  const tenantName = (store.currentItem as any)?.name ?? '';

  // Fetch settings on mount
  useEffect(() => {
    if (id) {
      store.GetTenantSettings(id).then((res: any) => {
        setLoadedSettings(res?.settings ?? {});
      }).catch(() => {});
    }
  }, [id]);

  // Populate local state from loaded settings
  useEffect(() => {
    {
      const s = loadedSettings ?? {};
      setSettings({
        require_2fa: s.require_2fa ?? false,
        sso_enabled: s.sso_enabled ?? false,
        allowed_domains: Array.isArray(s.allowed_domains) ? s.allowed_domains : [],
        allowed_ip_ranges: Array.isArray(s.allowed_ip_ranges) ? s.allowed_ip_ranges : [],
        email_notifications_enabled: s.email_notifications_enabled ?? true,
        slack_integration_enabled: s.slack_integration_enabled ?? false,
      });
    }
  }, [loadedSettings]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleSave = useCallback(
    async (cardName: string, patch: Partial<SecuritySettings>) => {
      if (!id) return;
      setSaving(cardName);
      setErrorMessage(null);
      setSuccessCard(null);
      try {
        await store.UpdateTenantSettings(id, {
          settings: { ...loadedSettings, ...settings, ...patch },
        });
        setSettings((prev) => ({ ...prev, ...patch }));
        setSuccessCard(cardName);
        setTimeout(() => setSuccessCard(null), 3000);
      } catch (err) {
        setErrorMessage(err);
      } finally {
        setSaving(null);
      }
    },
    [id, settings, loadedSettings, store, t],
  );

  const handleAddDomain = () => {
    const domain = newDomain.trim().toLowerCase();
    if (!domain) return;
    if (settings.allowed_domains.includes(domain)) return;
    setSettings((prev) => ({
      ...prev,
      allowed_domains: [...prev.allowed_domains, domain],
    }));
    setNewDomain('');
  };

  const handleRemoveDomain = (domain: string) => {
    setSettings((prev) => ({
      ...prev,
      allowed_domains: prev.allowed_domains.filter((d) => d !== domain),
    }));
  };

  const handleAddIpRange = () => {
    const range = newIpRange.trim();
    if (!range) return;
    if (settings.allowed_ip_ranges.includes(range)) return;
    setSettings((prev) => ({
      ...prev,
      allowed_ip_ranges: [...prev.allowed_ip_ranges, range],
    }));
    setNewIpRange('');
  };

  const handleRemoveIpRange = (range: string) => {
    setSettings((prev) => ({
      ...prev,
      allowed_ip_ranges: prev.allowed_ip_ranges.filter((r) => r !== range),
    }));
  };

  const handleDomainKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddDomain();
    }
  };

  const handleIpKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddIpRange();
    }
  };

  // ---------------------------------------------------------------------------
  // Loading / error states
  // ---------------------------------------------------------------------------

  if (store.isLoading && !store.currentItem) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  if (store.error && !store.currentItem) {
    return (
      <div className="space-y-2">
        <ErrorAlert error={store.error} />
        <Link to="/tenants" className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="text-sm">
        <Link to="/tenants" className="text-muted-foreground hover:text-foreground">
          {t('tenant.list')}
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to={`/tenants/${id}`} className="text-muted-foreground hover:text-foreground">
          {tenantName || t('tenant.details')}
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-muted-foreground">{t('tenant.settings')}</span>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{t('tenant.security.title')}</span>
      </nav>

      {/* Page title */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">{t('tenant.security.title')}</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {t('tenant.security.description')}
        </p>
      </div>

      {/* Global error */}
      {errorMessage ? (
        <ErrorAlert error={errorMessage} dismissible onDismiss={() => setErrorMessage(null)} />
      ) : null}

      {/* Store error */}
      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* ================================================================= */}
      {/* Authentication */}
      {/* ================================================================= */}
      <Card>
        <CardHeader>
          <CardTitle>{t('tenant.security.authentication.title')}</CardTitle>
          <CardDescription>
            {t('tenant.security.authentication.description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <Toggle
            checked={settings.require_2fa}
            onChange={(value) => setSettings((prev) => ({ ...prev, require_2fa: value }))}
            label={t('tenant.security.authentication.require2fa')}
            description={t('tenant.security.authentication.require2faDescription')}
          />
          <div className="border-t border-border pt-5">
            <Toggle
              checked={settings.sso_enabled}
              onChange={(value) => setSettings((prev) => ({ ...prev, sso_enabled: value }))}
              label={t('tenant.security.authentication.ssoEnabled')}
              description={t('tenant.security.authentication.ssoEnabledDescription')}
            />
            {settings.sso_enabled && (
              <div className="mt-3 ml-14">
                <Link
                  to={`/tenants/${id}/settings/sso`}
                  className="text-sm text-primary hover:underline"
                >
                  {t('tenant.security.authentication.configureSso')}
                </Link>
              </div>
            )}
          </div>
        </CardContent>
        <CardFooter className="flex items-center justify-between">
          {successCard === 'authentication' && (
            <span className="text-sm text-green-600 dark:text-green-400">
              {t('tenant.security.saved')}
            </span>
          )}
          {successCard !== 'authentication' && <span />}
          <Button
            onClick={() =>
              handleSave('authentication', {
                require_2fa: settings.require_2fa,
                sso_enabled: settings.sso_enabled,
              })
            }
            disabled={saving === 'authentication'}
          >
            {saving === 'authentication' ? t('common.saving') : t('common.save')}
          </Button>
        </CardFooter>
      </Card>

      {/* ================================================================= */}
      {/* Allowed Email Domains */}
      {/* ================================================================= */}
      <Card>
        <CardHeader>
          <CardTitle>{t('tenant.security.domains.title')}</CardTitle>
          <CardDescription>
            {t('tenant.security.domains.description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {settings.allowed_domains.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">
              {t('tenant.security.domains.empty')}
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {settings.allowed_domains.map((domain) => (
                <Badge key={domain} variant="secondary" className="flex items-center gap-1.5 pr-1">
                  <span>{domain}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveDomain(domain)}
                    className="ml-0.5 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-muted-foreground/20 transition-colors"
                    aria-label={`${t('common.remove')} ${domain}`}
                  >
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </Badge>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                type="text"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                onKeyDown={handleDomainKeyDown}
                placeholder="example.com"
              />
            </div>
            <Button
              type="button"
              variant="secondary"
              onClick={handleAddDomain}
              disabled={!newDomain.trim()}
            >
              {t('common.add')}
            </Button>
          </div>
        </CardContent>
        <CardFooter className="flex items-center justify-between">
          {successCard === 'domains' && (
            <span className="text-sm text-green-600 dark:text-green-400">
              {t('tenant.security.saved')}
            </span>
          )}
          {successCard !== 'domains' && <span />}
          <Button
            onClick={() =>
              handleSave('domains', { allowed_domains: settings.allowed_domains })
            }
            disabled={saving === 'domains'}
          >
            {saving === 'domains' ? t('common.saving') : t('common.save')}
          </Button>
        </CardFooter>
      </Card>

      {/* ================================================================= */}
      {/* IP Allowlist */}
      {/* ================================================================= */}
      <Card>
        <CardHeader>
          <CardTitle>{t('tenant.security.ipAllowlist.title')}</CardTitle>
          <CardDescription>
            {t('tenant.security.ipAllowlist.description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {settings.allowed_ip_ranges.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">
              {t('tenant.security.ipAllowlist.empty')}
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {settings.allowed_ip_ranges.map((range) => (
                <Badge key={range} variant="secondary" className="flex items-center gap-1.5 pr-1 font-mono">
                  <span>{range}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveIpRange(range)}
                    className="ml-0.5 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-muted-foreground/20 transition-colors"
                    aria-label={`${t('common.remove')} ${range}`}
                  >
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </Badge>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                type="text"
                value={newIpRange}
                onChange={(e) => setNewIpRange(e.target.value)}
                onKeyDown={handleIpKeyDown}
                placeholder="e.g. 192.168.1.0/24"
              />
            </div>
            <Button
              type="button"
              variant="secondary"
              onClick={handleAddIpRange}
              disabled={!newIpRange.trim()}
            >
              {t('common.add')}
            </Button>
          </div>
        </CardContent>
        <CardFooter className="flex items-center justify-between">
          {successCard === 'ipAllowlist' && (
            <span className="text-sm text-green-600 dark:text-green-400">
              {t('tenant.security.saved')}
            </span>
          )}
          {successCard !== 'ipAllowlist' && <span />}
          <Button
            onClick={() =>
              handleSave('ipAllowlist', { allowed_ip_ranges: settings.allowed_ip_ranges })
            }
            disabled={saving === 'ipAllowlist'}
          >
            {saving === 'ipAllowlist' ? t('common.saving') : t('common.save')}
          </Button>
        </CardFooter>
      </Card>

      {/* ================================================================= */}
      {/* Notifications */}
      {/* ================================================================= */}
      <Card>
        <CardHeader>
          <CardTitle>{t('tenant.security.notifications.title')}</CardTitle>
          <CardDescription>
            {t('tenant.security.notifications.description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <Toggle
            checked={settings.email_notifications_enabled}
            onChange={(value) =>
              setSettings((prev) => ({ ...prev, email_notifications_enabled: value }))
            }
            label={t('tenant.security.notifications.emailEnabled')}
            description={t('tenant.security.notifications.emailEnabledDescription')}
          />
          <div className="border-t border-border pt-5">
            <Toggle
              checked={settings.slack_integration_enabled}
              onChange={(value) =>
                setSettings((prev) => ({ ...prev, slack_integration_enabled: value }))
              }
              label={t('tenant.security.notifications.slackEnabled')}
              description={t('tenant.security.notifications.slackEnabledDescription')}
            />
          </div>
        </CardContent>
        <CardFooter className="flex items-center justify-between">
          {successCard === 'notifications' && (
            <span className="text-sm text-green-600 dark:text-green-400">
              {t('tenant.security.saved')}
            </span>
          )}
          {successCard !== 'notifications' && <span />}
          <Button
            onClick={() =>
              handleSave('notifications', {
                email_notifications_enabled: settings.email_notifications_enabled,
                slack_integration_enabled: settings.slack_integration_enabled,
              })
            }
            disabled={saving === 'notifications'}
          >
            {saving === 'notifications' ? t('common.saving') : t('common.save')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
});

export default TenantSecurityPage;
